import React, {useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {
    Button, ButtonTypes,
    Card, CardBody,
    CardHeader,
    customToast, DateFormats,
    DisabledContext, FormInput,
    SubTitle, Text,
    Title,
    Variants, WidthConstrainedContainer
} from "toolbox-react-frontend-components";
import {route} from "ziggy-js";
import {cloneDeep} from "lodash";
import LoadingPane from "../../../components/LoadingPane";

const CreateEditAlbum = (props) => {
    const {
        mode    = 'CREATE',
    } = props;

    const navigate = useNavigate();
    const { album } = useParams();

    const {
        disabled,
        setDisabled
    } = useContext(DisabledContext);

    const [data, setData] = useState({
        order:          '',
        title:          '',
        artist:         '',
        label:          '',
        album_cover:    '',
        spotify:        '',
        beat_port:      '',
        band_camp:      '',
        sound_cloud:    '',
    });
    const [loaded, setLoaded]           = useState(false);
    const [errors, setErrors]           = useState(null);
    const [submitting, setSubmitting]   = useState(false)

    useEffect(() => {
        loadPageData();
    }, []);

    useEffect(() => {
        if (submitting) {
            submit();
        }
    }, [submitting]);

    const loadPageData = () => {
        if (!!album) {
            axios.get(route('api.admin.album.get', {album: album}))
                .then((response) => {
                    if (!!response && !!response.data) {
                        setData(response.data);
                        setLoaded(true);
                        setSubmitting(false);
                        setDisabled(false);
                    } else {
                        customToast({
                            title:      'Page data not found. Please refresh the page to try again.',
                            variant:    Variants.Danger
                        });
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            setLoaded(true);
            setSubmitting(false);
            setDisabled(false);
        }
    }

    const submit = () => {
        setDisabled(true);
        setErrors(null);

        let url = (mode === 'EDIT') ? route('api.admin.album.update', {album: album}) : route('api.admin.album.store');

        axios({
            method: (mode === 'EDIT') ? 'PATCH' : 'POST',
            url:    url,
            data:   data
        }).then((response) => {
                customToast({
                    title:      (mode === 'EDIT') ? 'Album updated.' : 'Album created.',
                    variant:    Variants.Success
                });

                setSubmitting(false);
                setDisabled(false);

                navigate(route('admin.album.index', undefined, false));
            })
            .catch((error) => {
                if (!!error && !!error.response && !!error.response.data) {
                    if (!!error.response.data.message) {
                        customToast({
                            title:      error.response.data.message,
                            variant:    Variants.Danger
                        });
                    }

                    if (!!error.response.data.errors) {
                        setErrors(error.response.data.errors);
                    }

                    setSubmitting(false);
                    setDisabled(false);
                } else {
                    console.log(error);
                    customToast({
                        title:      'An error occurred. Please refresh the page to try again.',
                        variant:    Variants.Danger
                    });
                }
            });
    }

    const updateField = (fieldName, value) => {
        let clone = cloneDeep(data);

        clone[fieldName] = value;

        setData(clone);
    }

    if (!loaded) {
        return (
            <LoadingPane />
        );
    }

    return (
        <WidthConstrainedContainer>
            <Card noMargin={true} >
                <CardHeader
                    leftSlot={(mode === 'CREATE') ? (
                        <>
                            <Title title={'Create album: '} noMargin={true} />
                        </>
                    ) : (
                        <>
                            <Title title={'Edit album: '}/>
                            <SubTitle
                                title={data.title+' - '+data.artist+(!!data.label ? ' ('+data.label+')' : '')}
                                noMargin={true}
                            />
                        </>
                    )}
                />
                <CardBody>
                    <div className={'row m-0 p-0'}>
                        <div className="col-12 col-lg-6 m-0 p-0 pe-lg-2">
                            <FormInput
                                type={'text'}
                                label={'Title'}
                                name={'title'}
                                value={data.title ?? ''}
                                errorMessages={!!errors && !!errors.title ? errors.title : null}
                                onChange={(value) => updateField('title', value)}
                                disabled={submitting || disabled}
                            />
                        </div>
                        <div className="col-12 col-lg-6 m-0 p-0 ps-lg-2">
                            <FormInput
                                type={'text'}
                                label={'Artist'}
                                name={'artist'}
                                value={data.artist ?? ''}
                                errorMessages={!!errors && !!errors.artist ? errors.artist : null}
                                onChange={(value) => updateField('artist', value)}
                                disabled={submitting || disabled}
                            />
                        </div>
                    </div>
                    <FormInput
                        type={'text'}
                        label={'Label'}
                        name={'label'}
                        value={data.label ?? ''}
                        errorMessages={!!errors && !!errors.label ? errors.label : null}
                        onChange={(value) => updateField('label', value)}
                        disabled={submitting || disabled}
                    />
                    <Text>
                        <small>
                            This order field determines where in the homepage gallery the album will sit.
                            The higher the order, the higher in the gallery it will be.
                        </small>
                    </Text>
                    <FormInput
                        type={'number'}
                        label={'Order'}
                        name={'order'}
                        value={data.order ?? ''}
                        errorMessages={!!errors && !!errors.order ? errors.order : null}
                        onChange={(value) => updateField('order', value)}
                        disabled={submitting || disabled}
                        min={0}
                        step={1}
                    />
                    <FormInput
                        type={'single-image'}
                        label={'Album cover'}
                        name={'album_cover'}
                        value={data.album_cover ?? ''}
                        errorMessages={!!errors && !!errors.album_cover ? errors.album_cover : null}
                        onChange={(value) => updateField('album_cover', value)}
                        disabled={submitting || disabled}
                    />
                    <SubTitle
                        title={'External links'}
                        className={'mt-4'}
                    />
                    <Text>
                        <small>
                            Links do not need to be provided but will assist with site visibility.
                        </small>
                    </Text>
                    <FormInput
                        type={'text'}
                        label={'Spotify'}
                        name={'spotify'}
                        value={data.spotify ?? ''}
                        errorMessages={!!errors && !!errors.spotify ? errors.spotify : null}
                        onChange={(value) => updateField('spotify', value)}
                        disabled={submitting || disabled}
                    />
                    <FormInput
                        type={'text'}
                        label={'Beatport'}
                        name={'beat_port'}
                        value={data.beat_port ?? ''}
                        errorMessages={!!errors && !!errors.beat_port ? errors.beat_port : null}
                        onChange={(value) => updateField('beat_port', value)}
                        disabled={submitting || disabled}
                    />
                    <FormInput
                        type={'text'}
                        label={'Band Camp'}
                        name={'band_camp'}
                        value={data.band_camp ?? ''}
                        errorMessages={!!errors && !!errors.band_camp ? errors.band_camp : null}
                        onChange={(value) => updateField('band_camp', value)}
                        disabled={submitting || disabled}
                    />
                    <FormInput
                        type={'text'}
                        label={'Soundcloud'}
                        name={'sound_cloud'}
                        value={data.sound_cloud ?? ''}
                        errorMessages={!!errors && !!errors.sound_cloud ? errors.sound_cloud : null}
                        onChange={(value) => updateField('sound_cloud', value)}
                        disabled={submitting || disabled}
                    />
                    <div className={'d-flex align-items-center justify-content-between'}>
                        <Button
                            label={'Cancel'}
                            onClick={(event) => navigate(route('admin.album.index', undefined, false))}
                            disabled={submitting || disabled}
                            type={ButtonTypes.Link}
                            variant={Variants.Dark}
                        />
                        <Button
                            label={submitting ? 'Updating...' : 'Update'}
                            onClick={(event) => setSubmitting(true)}
                            disabled={submitting || disabled}
                        />
                    </div>
                </CardBody>
            </Card>
        </WidthConstrainedContainer>
    )
}

export default CreateEditAlbum;
